import React from 'react';
import LegalPage, { List, P, Placeholder, Section } from './LegalPage';

/**
 * GDPR-oriented Privacy Policy TEMPLATE.
 *
 * Structured around the Article 13 information obligations (identity of the
 * controller, purposes & legal bases, recipients, retention, data-subject
 * rights, right to complain to a supervisory authority, etc.). The content is
 * a sample only — see the in-page banner. Replace every <Placeholder> and have
 * a qualified adviser review before production use.
 */
const PrivacyPage: React.FC = () => {
  return (
    <LegalPage title="Privacy Policy" lastUpdated={<Placeholder>date</Placeholder>}>
      <Section heading="1. Who we are (Data Controller)">
        <P>
          This Privacy Policy explains how Sulis-Liiga (&quot;we&quot;, &quot;us&quot;, the
          &quot;Service&quot;) collects and processes your personal data when you register for and
          use the Sulis-Liiga badminton league management application.
        </P>
        <P>
          The data controller responsible for your personal data is{' '}
          <Placeholder>legal entity / organiser name</Placeholder>, registered at{' '}
          <Placeholder>registered address</Placeholder>
          {', '}
          <Placeholder>business ID, if any</Placeholder>. You can contact us regarding privacy
          matters at <Placeholder>privacy contact email</Placeholder>.
        </P>
        <P>
          We have <Placeholder>appointed / not appointed</Placeholder> a Data Protection Officer.
          Where appointed, they can be reached at <Placeholder>DPO contact, if any</Placeholder>.
        </P>
      </Section>

      <Section heading="2. What personal data we collect">
        <P>
          Depending on how you use the Service, we may process the following categories of data:
        </P>
        <List>
          <li>
            <strong>Account &amp; identity data:</strong> name, email address, and password (stored
            only in hashed form).
          </li>
          <li>
            <strong>Profile data:</strong> optional details you choose to add, such as age/date of
            birth, contact information, skill level, and privacy preferences.
          </li>
          <li>
            <strong>League activity data:</strong> teams you belong to, partner invitations, game
            proposals, scheduled games, results you submit or approve, disputes, points, and
            standings.
          </li>
          <li>
            <strong>Communications:</strong> messages and notifications you send or receive within
            the Service.
          </li>
          <li>
            <strong>Technical data:</strong> data required to operate and secure the Service, such
            as authentication tokens and, where applicable,{' '}
            <Placeholder>
              IP address, device/browser info, server logs — confirm what you log
            </Placeholder>
            .
          </li>
        </List>
      </Section>

      <Section heading="3. Why we process your data and our legal basis">
        <P>Under Article 6 of the GDPR we rely on the following legal bases for each purpose:</P>
        <List>
          <li>
            <strong>To create and manage your account and provide the Service</strong>{' '}
            (registration, login, teams, games, results, standings) —{' '}
            <em>performance of a contract</em> (Art. 6(1)(b)).
          </li>
          <li>
            <strong>To send service-related emails</strong> (email verification, password reset,
            game/notification alerts) — <em>performance of a contract</em> and our{' '}
            <em>legitimate interests</em> (Art. 6(1)(b) and (f)).
          </li>
          <li>
            <strong>To keep the Service secure and prevent abuse</strong> — our{' '}
            <em>legitimate interests</em> (Art. 6(1)(f)) in operating a safe platform.
          </li>
          <li>
            <strong>Optional profile fields and privacy preferences you actively provide</strong> —{' '}
            <em>consent</em> (Art. 6(1)(a)), which you may withdraw at any time via your profile.
          </li>
          <li>
            <strong>To comply with legal obligations</strong> where applicable —{' '}
            <em>legal obligation</em> (Art. 6(1)(c)).
          </li>
        </List>
        <P>
          Providing your account and identity data is necessary to use the Service; without it we
          cannot create an account for you. Optional profile data is exactly that — optional.
        </P>
      </Section>

      <Section heading="4. Who we share your data with (Recipients)">
        <P>We do not sell your personal data. We share it only as needed to run the Service:</P>
        <List>
          <li>
            <strong>Other users of the league:</strong> certain data is visible to other players by
            design — for example your display name, teams, game results, and standings. You can
            control some visibility (e.g. contact details, age) through your profile privacy
            preferences.
          </li>
          <li>
            <strong>Service providers (processors)</strong> acting on our instructions, such as{' '}
            <Placeholder>hosting provider, e.g. Railway / Cloudflare</Placeholder>,{' '}
            <Placeholder>database provider, e.g. MongoDB Atlas</Placeholder>, and{' '}
            <Placeholder>email provider</Placeholder>. These providers process data only to deliver
            their service to us under a data-processing agreement.
          </li>
          <li>
            <strong>Authorities or third parties</strong> where required by law or to protect our
            legal rights.
          </li>
        </List>
      </Section>

      <Section heading="5. International transfers">
        <P>
          Where our service providers process personal data outside the European Economic Area
          (EEA), we ensure an adequate level of protection through appropriate safeguards, such as
          the European Commission&apos;s Standard Contractual Clauses or an adequacy decision.
          Confirm the actual arrangements for each provider:{' '}
          <Placeholder>
            list processors that transfer data outside the EEA and the safeguard used
          </Placeholder>
          .
        </P>
      </Section>

      <Section heading="6. How long we keep your data (Retention)">
        <P>
          We keep your personal data only for as long as necessary for the purposes described above:
        </P>
        <List>
          <li>
            <strong>Account and profile data:</strong> for as long as your account is active, and
            deleted or anonymised within{' '}
            <Placeholder>retention period, e.g. 30–90 days</Placeholder> of account closure.
          </li>
          <li>
            <strong>League activity (games, results, standings):</strong> may be retained in
            aggregated or historical form to preserve the integrity of past seasons —{' '}
            <Placeholder>describe how long / in what form</Placeholder>.
          </li>
          <li>
            <strong>Technical logs:</strong> <Placeholder>log retention period</Placeholder>.
          </li>
        </List>
      </Section>

      <Section heading="7. Your rights under the GDPR">
        <P>Subject to the conditions in the GDPR, you have the right to:</P>
        <List>
          <li>access the personal data we hold about you (Art. 15);</li>
          <li>have inaccurate data corrected (Art. 16);</li>
          <li>
            have your data erased in certain circumstances (&quot;right to be forgotten&quot;, Art.
            17);
          </li>
          <li>restrict processing in certain circumstances (Art. 18);</li>
          <li>receive your data in a portable, machine-readable format (Art. 20);</li>
          <li>object to processing based on our legitimate interests (Art. 21);</li>
          <li>
            withdraw consent at any time, where processing is based on consent — without affecting
            processing carried out before withdrawal.
          </li>
        </List>
        <P>
          To exercise any of these rights, contact us at{' '}
          <Placeholder>privacy contact email</Placeholder>. We will respond within the time limits
          set by the GDPR (generally one month).
        </P>
      </Section>

      <Section heading="8. Right to lodge a complaint">
        <P>
          If you believe we have handled your personal data unlawfully, you have the right to lodge
          a complaint with a supervisory authority. In Finland this is the Office of the Data
          Protection Ombudsman (Tietosuojavaltuutetun toimisto),{' '}
          <span className="whitespace-nowrap">tietosuoja.fi</span>. You may also contact the
          supervisory authority in your country of residence.
        </P>
      </Section>

      <Section heading="9. Cookies and similar technologies">
        <P>
          The Service does not use cookies. To keep you logged in and to remember your preferences,
          we store a small amount of data in your browser&apos;s local storage: your authentication
          tokens, a cached copy of your account profile, your selected league, and your theme
          preference. This storage is strictly necessary to operate the Service and is not used for
          analytics or advertising. You can clear it at any time through your browser settings or by
          logging out.
        </P>
        <P>
          If we later introduce analytics or other non-essential cookies or storage, we will update
          this section and, where required, ask for your consent first.
        </P>
      </Section>

      <Section heading="10. Automated decision-making and profiling">
        <P>
          The Service calculates points and standings automatically from game results. This is a
          rules-based calculation and does not produce legal or similarly significant effects about
          you within the meaning of Article 22 of the GDPR. Confirm this remains accurate if you add
          any automated ranking, matchmaking, or eligibility decisions:{' '}
          <Placeholder>confirm no Art. 22 automated decisions, or describe them</Placeholder>.
        </P>
      </Section>

      <Section heading="11. Data security">
        <P>
          We take appropriate technical and organisational measures to protect your data, including
          hashed password storage, encrypted transport (HTTPS), access controls, and token-based
          authentication. No system is perfectly secure, but we work to protect your data against
          unauthorised access, loss, or misuse.
        </P>
      </Section>

      <Section heading="12. Children">
        <P>
          <Placeholder>
            State your minimum age and how you handle minors — e.g. whether under-16s (or the
            applicable Finnish age of consent for information-society services) may register, and
            whether guardian consent is required
          </Placeholder>
          .
        </P>
      </Section>

      <Section heading="13. Changes to this policy">
        <P>
          We may update this Privacy Policy from time to time. We will update the &quot;Last
          updated&quot; date above and, where changes are material, notify you through the Service
          or by email.
        </P>
      </Section>

      <Section heading="14. Contact us">
        <P>
          For any questions about this policy or your personal data, contact us at{' '}
          <Placeholder>privacy contact email</Placeholder>.
        </P>
      </Section>
    </LegalPage>
  );
};

export default PrivacyPage;
